import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import apiService from '../services/apiService';

export default function JobInventoryPanel({ collectionId, open }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({ assignments: [], active_count: 0 });
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ inventory_item_id: '', quantity: '1', notes: '' });

  const load = useCallback(async () => {
    if (!collectionId || !open) return;
    setLoading(true);
    setError('');
    try {
      const [inv, items] = await Promise.all([
        apiService.get(`collections/${collectionId}/inventory`),
        apiService.get('inventory-items?active_only=1'),
      ]);
      setData(inv || { assignments: [], active_count: 0 });
      setCatalog(Array.isArray(items) ? items : []);
    } catch (e) {
      setError(e.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [collectionId, open]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleAssign(e) {
    e.preventDefault();
    if (!form.inventory_item_id) return;
    setBusy(true);
    setError('');
    try {
      const res = await apiService.post(`collections/${collectionId}/inventory`, {
        inventory_item_id: Number(form.inventory_item_id),
        quantity: Number(form.quantity) || 1,
        notes: form.notes || null,
      });
      setData(res.data || res);
      setForm({ inventory_item_id: '', quantity: '1', notes: '' });
    } catch (err) {
      setError(err.message || 'Could not assign item');
    } finally {
      setBusy(false);
    }
  }

  async function markReturned(assignmentId) {
    setBusy(true);
    try {
      const res = await apiService.patch(`collections/${collectionId}/inventory/${assignmentId}/return`);
      setData(res.data || res);
    } catch (err) {
      setError(err.message || 'Could not mark returned');
    } finally {
      setBusy(false);
    }
  }

  async function removeAssignment(assignmentId) {
    if (!window.confirm('Remove this assignment record?')) return;
    setBusy(true);
    try {
      const res = await apiService.delete(`collections/${collectionId}/inventory/${assignmentId}`);
      setData(res.data || res);
    } catch (err) {
      setError(err.message || 'Could not remove');
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Equipment &amp; inventory
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Track skips, vehicles, and tools assigned to this job. Active on job: {data.active_count ?? 0}
      </Typography>

      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Box component="form" onSubmit={handleAssign} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200, flex: '1 1 200px' }}>
          <InputLabel id="inv-item-label">Asset</InputLabel>
          <Select
            labelId="inv-item-label"
            label="Asset"
            value={form.inventory_item_id}
            onChange={(e) => setForm((f) => ({ ...f, inventory_item_id: e.target.value }))}
          >
            <MenuItem value="">
              <em>Select…</em>
            </MenuItem>
            {catalog.map((it) => (
              <MenuItem key={it.id} value={String(it.id)}>
                {it.name}
                {it.sku ? ` (${it.sku})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Qty"
          type="number"
          value={form.quantity}
          onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
          sx={{ width: 72 }}
        />
        <TextField
          size="small"
          label="Notes"
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          sx={{ flex: '1 1 140px' }}
        />
        <Button type="submit" variant="contained" size="small" disabled={busy || !form.inventory_item_id}>
          Assign
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {(data.assignments || []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No assets assigned. Add items from the Inventory catalog first.
        </Typography>
      ) : (
        (data.assignments || []).map((row) => (
          <Box
            key={row.id}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {row.item?.name || 'Item'}
                {row.quantity > 1 ? ` ×${row.quantity}` : ''}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {row.item?.category ? <Chip size="small" label={row.item.category} /> : null}
                {row.is_active ? (
                  <Chip size="small" color="primary" label="On job" />
                ) : (
                  <Chip size="small" label="Returned" />
                )}
              </Box>
              {row.notes ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {row.notes}
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {row.is_active ? (
                <Button size="small" variant="outlined" onClick={() => markReturned(row.id)} disabled={busy}>
                  Returned
                </Button>
              ) : null}
              <IconButton size="small" aria-label="Delete" onClick={() => removeAssignment(row.id)} disabled={busy}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}
